<script lang="ts" setup>
import type { IMenuItem } from '@/api/interfaces'
import { BackgroundTheme } from '@/api/interfaces'
import { useEditorStore } from '@/stores/Editor'

import BurgerMenu from '@/components/public-page/BurgerMenu.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import GreensparkLogo from '@/components/icons/GreensparkLogo.vue'

interface Props {
  menuItemList?: IMenuItem[]
  buttons?: IMenuItem[]
}

defineProps<Props>()
const editorStore = useEditorStore()
</script>

<template>
  <header class="public-header">
    <v-container fluid>
      <div class="public-header-wrapper">
        <a href="https://www.getgreenspark.com" target="_blank">
          <GreensparkLogo
            :color="
              editorStore.editor.background.theme === BackgroundTheme.DARK ? 'white' : 'green'
            "
            height="32"
            width="160"
          />
        </a>
        <nav class="public-header-navigation">
          <ul class="public-header-navigation-list">
            <li
              v-for="(item, index) in menuItemList"
              :key="index"
              class="public-header-navigation-list-item"
            >
              <a
                :href="item.link"
                :style="{ color: item.color, backgroundColor: item.backgroundColor }"
                :target="item.target"
                class="public-header-navigation-list-item-link"
              >
                {{ item.label }}
              </a>
            </li>
          </ul>
          <div class="public-header-buttons">
            <LanguageSwitcher />
            <GsButton
              href="https://www.app.getgreenspark.com/login"
              target="_blank"
              type="secondary"
              width="135"
            >
              {{ $t('PublicHeader.buttons.login') }}
            </GsButton>
            <GsButton
              href="https://www.getgreenspark.com/account-type"
              target="_blank"
              type="success"
              width="135"
            >
              {{ $t('PublicHeader.buttons.register') }}
            </GsButton>
          </div>
        </nav>
        <BurgerMenu class="public-header-burger-menu" />
      </div>
    </v-container>
  </header>
</template>

<style lang="scss" scoped>
.public-header {
  position: fixed;
  top: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  width: 100%;
  background: var(--ui-white);
  box-shadow: 0 2px 10px 0 rgb(0 0 0 / 25%);
  height: 60px;

  @include media('md-and-up') {
    height: 70px;

    .v-container {
      display: flex;
      align-items: center;
      padding: 0;
    }
  }

  .dark & {
    background: var(--darkest);
  }
}

.public-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.public-header-navigation {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
}

.public-header-navigation-list {
  display: none;
  list-style-type: none;
  padding: 0;
  margin-left: 70px;

  @include media('md-and-up') {
    display: flex;
    gap: 24px;
  }
}

.public-header-navigation-list-item-link {
  display: block;
  height: 100%;
  text-transform: uppercase;
  text-decoration: none;
  font-size: 20px;
  font-weight: 700;

  &:hover {
    opacity: 0.8;
  }
}

.public-header-buttons {
  display: none;

  @include media('md-and-up') {
    display: flex;
    gap: 16px;
  }
}
</style>
